
import {
  getReading as getReadingService,
  getReadingByReadingId as getReadingByReadingIdService,
  patchCustomerProfileNl,
  saveReading as saveReadingService,
  saveReadingByReadingId as saveReadingByReadingIdService,
} from '@eneco/dc/services';
import { Usages_Reading_MeterSaveRequestModel } from '@monorepo-types/dc';
import { fromPromise } from 'xstate';

import { ReadingContext } from './MeterReadingsFlow.types';

const getMetersSaveModel = (meters: NonNullable<ReadingContext['meters']>): Usages_Reading_MeterSaveRequestModel[] =>
  Object.entries(meters).map(
    ([_, { connectionPointEan, counters, deviationReasonCode, deviationReasonExplanation }]) => ({
      connectionPointEan,
      counters: counters?.map(({ counterIdentification, counterType, value }) => ({
        counterIdentification,
        value: Number(value),
        counterType,
      })),
      deviationReasonCode,
      deviationReasonExplanation,
    }),
  );

const getReading = async ({ businessUnit, label, customerId, accountId }: ReadingContext) => {
  if (!customerId) throw new Error('missing customerId');
  if (!accountId) throw new Error('missing accountId');

  const response = await getReadingService({
    businessUnit,
    label,
    customerId,
    accountId,
  });

  return response.data;
};

const getReadingByReadingId = async ({ businessUnit, label, readingId, houseNumber }: ReadingContext) => {
  if (!readingId) throw new Error('missing readingId');
  if (!houseNumber) throw new Error('missing houseNumber');

  const response = await getReadingByReadingIdService({
    businessUnit,
    label,
    readingId,
    houseNumber,
  });

  return response.data;
};

const saveEmail = async ({ label, accountId, customerId, email, mobilePhoneNumber, phoneNumber }: ReadingContext) => {
  if (!accountId) throw new Error('missing accountId');
  if (!customerId) throw new Error('missing customerId');
  if (!email) throw new Error('missing email');

  return await patchCustomerProfileNl({
    label,
    customerId,
    requestBody: {
      data: {
        email,
        mobilePhoneNumber,
        phoneNumber,
        sendEmail: false,
      },
    },
  });
};

const saveReading = async ({
  businessUnit,
  label,
  accountId,
  customerId,
  readingId,
  houseNumber,
  meters,
}: ReadingContext) => {
  if (!accountId) throw new Error('missing accountId');
  if (!customerId) throw new Error('missing customerId');
  if (!readingId) throw new Error('missing readingId');
  if (!meters) throw new Error('missing meters');

  const response = await saveReadingService({
    businessUnit,
    label,
    accountId,
    customerId,
    requestBody: {
      data: {
        readingId,
        houseNumber,
        meters: getMetersSaveModel(meters),
      },
    },
  });

  return response.data;
};

const saveReadingByReadingId = async ({ businessUnit, label, readingId, houseNumber, meters }: ReadingContext) => {
  if (!readingId) throw new Error('missing readingId');
  if (!meters) throw new Error('missing meters');

  const response = await saveReadingByReadingIdService({
    businessUnit,
    label,
    requestBody: {
      data: {
        readingId,
        houseNumber,
        meters: getMetersSaveModel(meters),
      },
    },
  });

  return response.data;
};

export type getReadingOutput = Awaited<ReturnType<typeof getReading>>;
export type getReadingByReadingIdOutput = Awaited<ReturnType<typeof getReadingByReadingId>>;
export type saveReadingOutput = Awaited<ReturnType<typeof saveReading>>;
export type saveReadingByReadingIdOutput = Awaited<ReturnType<typeof saveReadingByReadingId>>;
export type saveEmailOutput = Awaited<ReturnType<typeof saveEmail>>;

export const actors = {
  SAVE_EMAIL: fromPromise<saveEmailOutput, ReadingContext>(async ({ input }) => await saveEmail(input)),
  SAVE_READING: fromPromise<saveReadingOutput, ReadingContext>(async ({ input }) => await saveReading(input)),
  SAVE_READING_BY_READING_ID: fromPromise<saveReadingByReadingIdOutput, ReadingContext>(
    async ({ input }) => await saveReadingByReadingId(input),
  ),
  GET_READING: fromPromise<getReadingOutput, ReadingContext>(async ({ input }) => await getReading(input)),
  GET_READING_BY_READING_ID: fromPromise<getReadingByReadingIdOutput, ReadingContext>(
    async ({ input }) => await getReadingByReadingId(input),
  ),
};
